/**
 * useWorkspaceHandlers Hook
 * 
 * Handles workspace-level operations:
 * - Save workspace name
 * - Change visibility mode
 * - Upload/remove logo
 */

import React, { ChangeEvent } from "react";
import { auth, db, storage } from "../../../firebase";
import { doc, updateDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { Workspace, ValuesVisibilityMode, TimeFormat, DateFormat, ThemeMode } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { DemoModeMessage } from "../../../utils/demoModeMessage";

export interface UseWorkspaceHandlersParams {
  workspaceId: string;
  workspace: Workspace | null;
  canManageWorkspaceConfig: () => boolean;
  setWorkspace: React.Dispatch<React.SetStateAction<Workspace | null>>;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  setUploadingLogo: React.Dispatch<React.SetStateAction<boolean>>;
  setRemoveLogoDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbarElement: React.Dispatch<React.SetStateAction<React.ReactElement | null>>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}

export interface UseWorkspaceHandlersReturn {
  handleSaveName: (newName: string) => Promise<void>;
  handleVisibilityModeChange: (mode: ValuesVisibilityMode) => Promise<void>;
  handleTimeFormatChange: (format: TimeFormat) => Promise<void>;
  handleDateFormatChange: (format: DateFormat) => Promise<void>;
  handleThemeModeChange: (mode: ThemeMode) => Promise<void>;
  handleSaveTransactionIdPrefix: (prefix: string) => Promise<void>;
  handleAllowExpenseFeeChange: (enabled: boolean) => Promise<void>;
  handleAllowTransferFeeChange: (enabled: boolean) => Promise<void>;
  handleLogoUpload: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleRemoveLogoClick: () => void;
  handleRemoveLogoConfirm: () => Promise<void>;
}

/**
 * Hook for workspace-level handlers
 */
export function useWorkspaceHandlers({
  workspaceId,
  workspace,
  canManageWorkspaceConfig,
  setWorkspace,
  setSaving,
  setUploadingLogo,
  setRemoveLogoDialogOpen,
  setSnackbarOpen,
  setSnackbarElement,
  fileInputRef,
}: UseWorkspaceHandlersParams): UseWorkspaceHandlersReturn {
  const handleSaveName = async (newName: string): Promise<void> => {
    if (!workspace || !canManageWorkspaceConfig()) return;

    // DEMO MODE: Block in production, allow in development
    // PRODUCTION MODE: Continue with normal save flow
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Workspace name cannot be edited. Please log in to edit workspace settings." />
      );
      setSnackbarOpen(true);
      // Return (don't throw) so EditableTextField exits edit mode
      return;
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, "workspaces", workspaceId), {
        name: newName,
      });
    } catch (error) {
      console.error("Error updating workspace name:", error);
      alert("Failed to update workspace name");
      throw error; // Re-throw so EditableTextField can handle it
    } finally {
      setSaving(false);
    }
  };

  const handleVisibilityModeChange = async (mode: ValuesVisibilityMode): Promise<void> => {
    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Visibility mode cannot be changed. Please log in to change workspace settings." />
      );
      setSnackbarOpen(true);
      return;
    }

    if (!workspaceId || !auth.currentUser || !canManageWorkspaceConfig()) return;

    try {
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      await updateDoc(settingsRef, { valuesVisibilityMode: mode });
    } catch (error) {
      console.error("Error updating visibility mode:", error);
      alert("Failed to update visibility mode");
    }
  };

  const handleTimeFormatChange = async (format: TimeFormat): Promise<void> => {
    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Time format cannot be changed. Please log in to change workspace settings." />
      );
      setSnackbarOpen(true);
      return;
    }

    if (!workspaceId || !auth.currentUser || !canManageWorkspaceConfig()) return;

    try {
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      await updateDoc(settingsRef, { timeFormat: format });
    } catch (error) {
      console.error("Error updating time format:", error);
      alert("Failed to update time format");
    }
  };

  const handleDateFormatChange = async (format: DateFormat): Promise<void> => {
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Date format cannot be changed. Please log in to change workspace settings." />
      );
      setSnackbarOpen(true);
      return;
    }

    if (!workspaceId || !auth.currentUser || !canManageWorkspaceConfig()) return;

    try {
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      await updateDoc(settingsRef, { dateFormat: format });
    } catch (error) {
      console.error("Error updating date format:", error);
      alert("Failed to update date format");
    }
  };

  const handleThemeModeChange = async (mode: ThemeMode): Promise<void> => {
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Theme cannot be changed. Please log in to change workspace settings." />
      );
      setSnackbarOpen(true);
      return;
    }

    if (!workspaceId || !auth.currentUser || !canManageWorkspaceConfig()) return;

    try {
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      await setDoc(settingsRef, { themeMode: mode }, { merge: true });
    } catch (error) {
      console.error("Error updating theme mode:", error);
      alert("Failed to update theme");
    }
  };

  const handleSaveTransactionIdPrefix = async (prefix: string): Promise<void> => {
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Transaction ID prefix cannot be changed. Please log in to edit workspace settings." />
      );
      setSnackbarOpen(true);
      return;
    }
    if (!workspaceId || !auth.currentUser || !canManageWorkspaceConfig()) return;
    const trimmed = prefix.trim();
    if (!trimmed) {
      alert("Transaction ID prefix is required and cannot be empty.");
      throw new Error("Transaction ID prefix is required");
    }
    setSaving(true);
    try {
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      await updateDoc(settingsRef, { transactionIdPrefix: trimmed });
    } catch (error) {
      console.error("Error updating transaction ID prefix:", error);
      alert("Failed to update transaction ID prefix");
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleAllowExpenseFeeChange = async (enabled: boolean): Promise<void> => {
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Expense fee setting cannot be changed. Please log in to edit workspace settings." />
      );
      setSnackbarOpen(true);
      return;
    }
    if (!workspaceId || !auth.currentUser || !canManageWorkspaceConfig()) return;
    try {
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      await setDoc(settingsRef, { allowExpenseFee: enabled }, { merge: true });
    } catch (error) {
      console.error("Error updating allow expense fee:", error);
      alert("Failed to update expense fee setting");
    }
  };

  const handleAllowTransferFeeChange = async (enabled: boolean): Promise<void> => {
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Transfer fee setting cannot be changed. Please log in to edit workspace settings." />
      );
      setSnackbarOpen(true);
      return;
    }
    if (!workspaceId || !auth.currentUser || !canManageWorkspaceConfig()) return;
    try {
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      await setDoc(settingsRef, { allowTransferFee: enabled }, { merge: true });
    } catch (error) {
      console.error("Error updating allow transfer fee:", error);
      alert("Failed to update transfer fee setting");
    }
  };

  const handleLogoUpload = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file || !workspace || !canManageWorkspaceConfig()) return;

    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Logo cannot be uploaded. Please log in to upload workspace logo." />
      );
      setSnackbarOpen(true);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    setUploadingLogo(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User is not authenticated");
      }

      // Delete old logo if exists
      if (workspace.logoUrl) {
        try {
          const oldLogoRef = ref(storage, workspace.logoUrl);
          await deleteObject(oldLogoRef);
        } catch (error) {
          // Ignore error if file doesn't exist
        }
      }

      // Upload new logo
      const logoPath = `workspaces/${workspaceId}/logo/${Date.now()}_${file.name}`;

      const logoRef = ref(storage, logoPath);
      await uploadBytes(logoRef, file);
      
      const downloadURL = await getDownloadURL(logoRef);

      // Update workspace document
      await updateDoc(doc(db, "workspaces", workspaceId), {
        logoUrl: downloadURL,
      });
    } catch (error: any) {
      console.error("Error uploading logo:", error);

      // Check if it's a permission error
      if (error?.code === "storage/unauthorized" || error?.code === "storage/permission-denied") {
        alert(
          `⚠️ PERMISSION ERROR: Failed to upload logo.\n\n` +
          `Error: ${error?.message || "Unknown error"}\n` +
          `Code: ${error?.code}\n\n` +
          `This usually means:\n` +
          `1. Storage rules have not been deployed - Run: firebase deploy --only storage\n` +
          `2. User is not the workspace owner\n` +
          `3. Storage rules need to be updated\n\n` +
          `Please check the browser console for more details.`
        );
      } else {
        alert(`Failed to upload logo: ${error?.message || "Unknown error"}`);
      }
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveLogoClick = (): void => {
    setRemoveLogoDialogOpen(true);
  };

  const handleRemoveLogoConfirm = async (): Promise<void> => {
    if (!workspace?.logoUrl || !canManageWorkspaceConfig()) return;

    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Logo cannot be removed. Please log in to remove workspace logo." />
      );
      setSnackbarOpen(true);
      setRemoveLogoDialogOpen(false);
      return;
    }

    setUploadingLogo(true);
    setRemoveLogoDialogOpen(false);
    try {
      const logoRef = ref(storage, workspace.logoUrl);
      await deleteObject(logoRef);

      await updateDoc(doc(db, "workspaces", workspaceId), {
        logoUrl: null,
      });
    } catch (error: any) {
      console.error("Error removing logo:", error);
      alert(`Failed to remove logo: ${error?.message || "Unknown error"}`);
    } finally {
      setUploadingLogo(false);
    }
  };

  return {
    handleSaveName,
    handleVisibilityModeChange,
    handleTimeFormatChange,
    handleDateFormatChange,
    handleThemeModeChange,
    handleSaveTransactionIdPrefix,
    handleAllowExpenseFeeChange,
    handleAllowTransferFeeChange,
    handleLogoUpload,
    handleRemoveLogoClick,
    handleRemoveLogoConfirm,
  };
}
